// Signing in: Apple, Google, or email and password, with Create Account and Forgot Password. The buttons call the
// functions passed in (Firebase Auth for real, the demo signs in at once).

import { useState, type FormEvent } from "react";
import { t } from "../i18n";
import { Icon } from "./Icon";
import { LegalLinks } from "./legal";
import type { AuthActions } from "./model";

type Mode = "signIn" | "create" | "forgot";

export function SignIn({ auth }: { auth: AuthActions }) {
  const [mode, setMode] = useState<Mode>("signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);

  const run = async (action: () => Promise<void>, done?: string) => {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      if (done) setMessage({ text: done, error: false });
    } catch (error) {
      setMessage({ text: error instanceof Error && error.message ? error.message : t("That didn't work. Please try again."), error: true });
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (mode === "forgot") return void run(() => auth.resetPassword(email.trim()), t("If there's an account for {email}, a mail to set a new password is on its way.", { email: email.trim() }));
    if (!email.trim() || !password) return setMessage({ text: t("Enter your email and password."), error: true });
    return void run(() => (mode === "create" ? auth.createAccount(email.trim(), password) : auth.signInWithEmail(email.trim(), password)));
  };

  return (
    <div className="signin">
      <main className="signin-card" aria-labelledby="signin-title">
        <img src="/icon.png" alt="" width={76} height={76} />
        <h1 id="signin-title">Todolix</h1>
        <p className="tag">{t("To-dos. Beautifully done.")}</p>
        {mode !== "forgot" && (
          <>
            <button className="provider apple" disabled={busy} onClick={() => void run(auth.signInWithApple)}><Icon name="apple" />{t("Continue with Apple")}</button>
            <button className="provider google" disabled={busy} onClick={() => void run(auth.signInWithGoogle)}><Icon name="google" />{t("Continue with Google")}</button>
            <div className="or">{t("or")}</div>
          </>
        )}
        <form onSubmit={submit} noValidate>
          <input className="field" type="email" placeholder={t("Email")} aria-label={t("Email")} autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          {mode !== "forgot" && (
            <input className="field" type="password" placeholder={t("Password")} aria-label={t("Password")} autoComplete={mode === "create" ? "new-password" : "current-password"}
              value={password} onChange={(event) => setPassword(event.target.value)} />
          )}
          <button className="mint-button" type="submit" disabled={busy}>
            {mode === "create" ? t("Create Account") : mode === "forgot" ? t("Send Link") : t("Sign In")}
          </button>
        </form>
        {message && <p className={`signin-message${message.error ? " error" : ""}`} role={message.error ? "alert" : "status"}>{message.text}</p>}
        <div className="signin-links">
          {mode === "signIn" ? (
            <>
              <button onClick={() => { setMode("forgot"); setMessage(null); }}>{t("Forgot Password?")}</button>
              <button onClick={() => { setMode("create"); setMessage(null); }}>{t("Create Account")}</button>
            </>
          ) : (
            <button onClick={() => { setMode("signIn"); setMessage(null); }}>{t("Back to Sign In")}</button>
          )}
        </div>
        <p className="signin-foot">{t("The same account as on your iPhone, iPad and Mac.")}</p>
        <LegalLinks className="signin-legal" />
      </main>
    </div>
  );
}
